import type { Material } from "@/types/material";

/**
 * An ingredient owned by no tenant — a row in the catalogue a super admin
 * maintains on everyone's behalf. Every organization sees it and can build
 * recipes on it; none of them may change it.
 *
 * This is the `globalWhenUnscoped` rule the API's ResourceOwnershipGuard applies
 * to `material` (T-102a), restated so the UI stops offering writes the server
 * will refuse. Keep the two in step: a write here 403s.
 *
 * Do not confuse it with `companyId == null`, which means "shared across the
 * concepts inside one organization" and carries no such restriction — see
 * `filterMaterialsByConcept`.
 *
 * `undefined` is not `null`: some list responses omit the field, and an absent
 * field is not a claim that the row is global, so those stay editable.
 */
export function isSharedCatalogueMaterial(
  material: Pick<Material, "organizationId">,
): boolean {
  return material.organizationId === null;
}

/**
 * Whether the current user may edit, deactivate, delete, or add sub-ingredients
 * to this ingredient. Only the shared catalogue restricts anyone; everything
 * else is already scoped to the tenant by the time it reaches the client.
 */
export function canEditMaterial(
  material: Pick<Material, "organizationId">,
  isSuperAdmin: boolean,
): boolean {
  return isSuperAdmin || !isSharedCatalogueMaterial(material);
}
